import React from 'react';
import { Box, Typography, Grow, Chip } from '@mui/material';
import { InfoOutlined, ErrorOutline, FileUpload, LockOutlined, ContentCopyOutlined } from '@mui/icons-material';

interface ErrorSectionProps {
  error: string;
}

export const ErrorSection: React.FC<ErrorSectionProps> = ({ error }) => {
  if (!error) return null;

  // 🔒 DETECT: Password-protected PDF scenarios
  const lower = (error || '').toLowerCase();
  const isPasswordProtectedError =
    error?.includes('🔒') ||
    lower.includes('password-protected') ||
    lower.includes('password protected') ||
    lower.includes('incorrect password') ||
    lower.includes('invalid encryption key length') ||
    lower.includes('need user password') ||
    lower.includes('encrypted') ||
    lower.includes('permission') ||
    error?.includes('💡 Solutions:');

  // 🎯 DETECT: No text extractable scenarios for better UX
  const isNoTextError = error?.includes('no extractable text') || 
                        error?.includes('contain no extractable text') ||
                        error?.includes('scanned image') ||
                        error?.includes('empty document');

  // 🎨 CONDITIONAL STYLING: Different appearance for password, no-text, and technical errors
  const styles = isPasswordProtectedError ? {
    container: {
      bgcolor: 'rgba(245, 158, 11, 0.10)',
      border: '1px solid rgba(245, 158, 11, 0.35)',
      borderLeft: '4px solid #f59e0b'
    },
    icon: <LockOutlined sx={{ color: '#f59e0b', fontSize: '1.2rem' }} />,
    titleColor: '#92400e',
    textColor: '#78350f',
    title: 'Password-Protected Files Detected',
    isPasswordError: true
  } : isNoTextError ? {
    container: {
      bgcolor: 'rgba(14, 165, 233, 0.10)',
      border: '1px solid rgba(14, 165, 233, 0.35)',
      borderLeft: '4px solid #0ea5e9'
    },
    icon: <InfoOutlined sx={{ color: '#0ea5e9', fontSize: '1.2rem' }} />,
    titleColor: '#0c4a6e',
    textColor: '#075985',
    title: 'No Text Content Found',
    isPasswordError: false
  } : {
    container: {
      bgcolor: 'rgba(220, 38, 38, 0.10)',
      border: '1px solid rgba(248, 113, 113, 0.35)',
      borderLeft: '4px solid #dc2626'
    },
    icon: <ErrorOutline sx={{ color: '#dc2626', fontSize: '1.2rem' }} />,
    titleColor: '#dc2626',
    textColor: '#991b1b',
    title: 'Processing Error',
    isPasswordError: false
  };

  return (
    <Grow in={!!error} timeout={300}>
      <Box sx={{ 
        mt: 2, 
        p: 2.5, 
        borderRadius: 2,
        ...styles.container
      }}>
        {/* Header with Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {styles.icon}
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: styles.titleColor, 
              fontWeight: 600,
              fontSize: '0.95rem',
              ml: 1
            }}
          >
            {styles.title}
          </Typography>
        </Box>

        {/* Error Message */}
        <Typography 
          variant="body2" 
          sx={{ 
            color: styles.textColor, 
            fontSize: '0.875rem',
            lineHeight: 1.5,
            mb: isNoTextError ? 2 : 0
          }}
        >
          {error}
        </Typography>

        {/* 💡 HELPFUL SUGGESTIONS: Show for password-protected and no-text scenarios */}
        {(isPasswordProtectedError || isNoTextError) && (
          <Box sx={{ mt: 2 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: styles.textColor, 
                fontSize: '0.8rem',
                fontWeight: 500,
                mb: 1
              }}
            >
              💡 Quick Solutions:
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {isPasswordProtectedError ? (
                // Password-protected file suggestions
                <>
                  <Chip 
                    icon={<LockOutlined sx={{ fontSize: '0.9rem' }} />}
                    label="Remove password protection" 
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.75rem',
                      borderColor: '#f59e0b',
                      color: styles.textColor,
                      '&:hover': { bgcolor: '#fef3c7' }
                    }}
                  />
                  <Chip 
                    icon={<ContentCopyOutlined sx={{ fontSize: '0.9rem' }} />}
                    label="Print and scan as new PDF" 
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.75rem',
                      borderColor: '#f59e0b',
                      color: styles.textColor,
                      '&:hover': { bgcolor: '#fef3c7' }
                    }}
                  />
                  <Chip 
                    label="Convert to Word document" 
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.75rem',
                      borderColor: '#f59e0b',
                      color: styles.textColor,
                      '&:hover': { bgcolor: '#fef3c7' }
                    }}
                  />
                </>
              ) : (
                // No-text file suggestions
                <>
                  <Chip 
                    icon={<FileUpload sx={{ fontSize: '0.9rem' }} />}
                    label="Upload text-based PDFs" 
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.75rem',
                      borderColor: '#0ea5e9',
                      color: styles.textColor,
                      '&:hover': { bgcolor: '#e0f2fe' }
                    }}
                  />
                  <Chip 
                    label="Convert scanned documents to text" 
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.75rem',
                      borderColor: '#0ea5e9',
                      color: styles.textColor,
                      '&:hover': { bgcolor: '#e0f2fe' }
                    }}
                  />
                  <Chip 
                    label="Use Word documents instead" 
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.75rem',
                      borderColor: '#0ea5e9',
                      color: styles.textColor,
                      '&:hover': { bgcolor: '#e0f2fe' }
                    }}
                  />
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Grow>
  );
}; 